import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CONFIG from '../config';
import { verifyToken } from '../services/api';
import './LoginPage.css';

function LoginPage({ onLogin }) {
  const navigate = useNavigate();
  const buttonRef = useRef(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if Google Identity Services script is loaded
    const checkGoogleScript = () => {
      if (window.google && window.google.accounts && window.google.accounts.id) {
        setScriptLoaded(true);
        initializeGoogleSignIn();
      } else {
        // Script not loaded yet, wait a bit and try again
        setTimeout(checkGoogleScript, 100);
      }
    };

    const initializeGoogleSignIn = () => {
      try {
        // Validate Client ID
        if (!CONFIG.GOOGLE_CLIENT_ID || CONFIG.GOOGLE_CLIENT_ID === 'YOUR_CLIENT_ID.apps.googleusercontent.com') {
          setError('Google Client ID not configured. Please check your .env file.');
          console.error('Google Client ID is missing or not set correctly');
          return;
        }

        console.log('Initializing Google Sign-In with Client ID:', CONFIG.GOOGLE_CLIENT_ID);

        window.google.accounts.id.initialize({
          client_id: CONFIG.GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
        });

        // Render the button
        if (buttonRef.current) {
          window.google.accounts.id.renderButton(
            buttonRef.current,
            {
              theme: 'filled_blue',
              size: 'large',
              text: 'signin_with',
              width: 300,
            }
          );
          console.log('Google Sign-In button rendered successfully');
        } else {
          console.error('Button ref is null');
        }
      } catch (err) {
        console.error('Error initializing Google Sign-In:', err);
        setError('Failed to initialize Google Sign-In. Check console for details.');
      }
    };

    // Start checking for script
    checkGoogleScript();

    // Timeout after 10 seconds if script doesn't load
    const timeout = setTimeout(() => {
      if (!scriptLoaded) {
        setError('Google Sign-In script failed to load. Please refresh the page.');
        console.error('Google Identity Services script did not load within 10 seconds');
      }
    }, 10000);

    return () => clearTimeout(timeout);
  }, [scriptLoaded]);

  const handleCredentialResponse = async (response) => {
    try {
      console.log('Received credential, verifying token...');
      // Send ID token to backend for verification
      const result = await verifyToken(response.credential);
      console.log('Token verification result:', result);

      if (result.success && result.student) {
        // Store student data with stats
        const studentData = {
          ...result.student,
          stats: result.stats,
        };
        onLogin(studentData);
        navigate('/dashboard');
      } else {
        const errorMsg = result.error || 'Login failed. Please try again.';
        console.error('Login failed:', errorMsg, result);
        alert(errorMsg);
        if (result.message) {
          alert(result.message);
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        backendUrl: CONFIG.BACKEND_URL,
        backendType: CONFIG.BACKEND_TYPE,
      });
      
      // Show more detailed error message
      let errorMessage = 'An error occurred during login. Please try again.';
      if (error.message) {
        if (error.message.includes('Network error') || error.message.includes('Failed to fetch')) {
          errorMessage = `Cannot connect to server. Please check:\n1. Backend URL: ${CONFIG.BACKEND_URL}\n2. Backend is running\n3. Network connection`;
        } else {
          errorMessage = error.message;
        }
      }
      alert(errorMessage);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <h1>Winter Reading Streak</h1>
        <p className="subtitle">Get cozy with a book for 30 minutes every day to keep up your streak all winter long.</p>
        
        <div className="login-box">
          <h2>Sign in with Google</h2>
          <p>Use your school Google account to continue</p>
          
          {error && (
            <div className="error-message">
              <p style={{ color: 'red', marginBottom: '10px' }}>{error}</p>
              <p style={{ fontSize: '0.9rem', color: '#666' }}>
                Troubleshooting:
                <br />1. Check browser console (F12) for errors
                <br />2. Verify REACT_APP_GOOGLE_CLIENT_ID in .env file
                <br />3. Ensure OAuth consent screen is configured
                <br />4. Try refreshing the page
              </p>
            </div>
          )}
          
          {!scriptLoaded && !error && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <p>Loading Google Sign-In...</p>
            </div>
          )}
          
          <div ref={buttonRef} className="google-signin-button"></div>
          
          <div className="login-info">
            <p>This app tracks your daily reading time and helps you keep up a reading habit. Have fun!</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;

